import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';

// Browser spellcheck can change the DOM without updating react-final-form state.
// On the next render, controlled inputs snap back to the stale value.
// These helpers copy the DOM value into form state when it differs.
//
// isDomValueSyncField — text <input> types and <textarea> only; excludes
//   checkbox, radio, date, number, etc.
// useDomValueSyncHandlers — for native fields (via Html5ValidationField)
// MentionsInput cannot use this path: its DOM value is plain text while form
// state is mention markup. Spellcheck for that field is handled in
// use-mention-dom-sync.js.

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel']);

export function isDomValueSyncField(component, type) {
  if (component === 'textarea') {
    return true;
  }
  if (component === 'input') {
    return !type || TEXT_INPUT_TYPES.has(type);
  }

  return false;
}

function useInputRef(input) {
  const inputRef = useRef(null);

  inputRef.current = input;

  return inputRef;
}

function syncFromEvent(inputRef, event) {
  const { value, onChange } = inputRef.current;

  if (event.target.value !== value) {
    onChange(event);
  }
}

export function useDomValueSyncHandlers(input) {
  const inputRef = useInputRef(input);

  const onInput = useCallback(
    event => {
      syncFromEvent(inputRef, event);
    },
    [inputRef]
  );

  const onBlur = useCallback(
    event => {
      syncFromEvent(inputRef, event);
      inputRef.current.onBlur(event);
    },
    [inputRef]
  );

  return { onInput, onBlur };
}

export function DomValueSyncInput({
  component: Component = 'input',
  input,
  ...rest
}) {
  const { onInput, onBlur } = useDomValueSyncHandlers(input);
  const value =
    input.value === undefined || input.value === null
      ? ''
      : String(input.value);

  return (
    <Component
      {...input}
      {...rest}
      value={value}
      onInput={onInput}
      onBlur={onBlur}
    />
  );
}

DomValueSyncInput.propTypes = {
  component: PropTypes.elementType,
  input: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired
  }).isRequired
};
